import {
  AbstractMesh,
  Animation,
  Color3,
  EasingFunction,
  EngineStore,
  Material,
  Scene,
  TransformNode,
  Vector2,
  Vector3,
  Viewport,
} from '@babylonjs/core';

//-------------------------------------------------
// * 기준 프레임레이트
const ANIMATION_TARGET_FRAME = 30;
// * Target을 제한하기 위한 Type입니다.
type AnimationTarget = AbstractMesh | Material | Viewport | TransformNode | any;
// * KeyFrame의 From/To Value의 타입을 추론하고 제한하기 위한 Type입니다.
type AnimationValue = number | Vector2 | Vector3 | Color3;
type AnimationParametaer = {
  targetProperty: string;
};
//-------------------------------------------------
/**
 * #### Animation 입력 Type
 * * target : AnimationGroup을 적용할 1개 이상의 타겟입니다. (AbstractMesh | Material | Camera ...)
 * * property : AnimationGroup을 구성할 1개 이상의 property입니다. 각 property 별로 Animation을 생성해 적용합니다.
 * * easingFunction : 이 값을 입력하면, 모든 Animation에 해당 easingFunction을 적용합니다.
 * * onAnimationEnd : 이 값을 입력하면, 모든 Animation이 끝날 때 입력한 콜백함수를 호출합니다.
 * 
 * @example
 * #### 단일 Property
 * ```ts
 *  {
 *    target: mesh.material,
 *    property: {
 *      targetProperty: 'albedoColor',
 *      keyFrame: {
 *        duration: 0.6,
 *        from: mesh.material.albedoColor,
 *        to: this.disableColor,
 *      },
 *    },
 *    easingFunction: createEasingFunction(
 *                      EasingFunctionType.QuarticEase, 
 *                      EasingModeType.In)
 *  }
 * ```
 * #### 다중 Property
 * ```ts
 *  {
 *    target: mesh.material,
 *    property: [
        {
          targetProperty: 'target',
          keyFrame: {
            duration: 0.6,
            from: camera.target,
            to: target,
          },
        },
        {
          targetProperty: 'beta',
          keyFrame: {
            duration: 0.6,
            from: camera.beta,
            to: beta,
          },
        },
      ],
      easingFunction: this.getEasingFunction()
 *  }
 * ```
 */
export type AnimationInput<T> = {
  target: T | T[];
  property: AnimationProperty<T> | AnimationProperty<T>[];
  easingFunction?: EasingFunction;
  onAnimationEnd?: () => void;
};

//-------------------------------------------------
/**
 * #### Property 입력 Type
 * * targetProperty : Animation의 타겟 property의 이름입니다.
 * * keyFrame : Animation을 구성할 1개 이상의 KeyFrame입니다.
 */
export type AnimationProperty<T> = {
  targetProperty: keyof T;
  keyFrame: AnimationKeyFrame | AnimationKeyFrame[];
};

//-------------------------------------------------
/**
 * #### KeyFrame 입력 Type
 * * duration : 이 값을 입력 시 시간을 frame으로 계산해 적용합니다. (seconds 기준)
 * * frame : 이 값을 입력 시 frame으로 적용합니다. (단, duration이 우선순위 높음)
 * * from : 시작 값입니다.
 * * to : 끝 값입니다. (from과 to의 type이 동일해야 합니다.)
 */
export interface AnimationKeyFrame {
  duration?: number;
  frame?: number;
  from: AnimationValue;
  to: AnimationValue;
}

//-------------------------------------------------
/**
 * #### AnimationGroup을 생성하고 실행하는 함수입니다.
 *
 * @example
 * ```ts
 *  playAnimation(
 *    this.prop.scene,
 *    {
 *      target: mesh.material,
 *      property: {
 *        targetProperty: 'albedoColor',
 *        keyFrame: {
 *          duration: 0.6,
 *          from: mesh.material.albedoColor,
 *          to: this.disableColor,
 *        },
 *      easingFunction: createEasingFunction(
 *                        EasingFunctionType.QuarticEase,
 *                        EasingModeType.In),
 *      onAnimationEnd: () => {
 *          console.log('onAnimationEnd');
 *        }
 *      }
 *    });
 * ```
 */
export const playAnimation = async <T extends AnimationTarget>(
  input: AnimationInput<T>,
  scene?: Scene,
  stopCurrent: boolean = false,
  loop: boolean = false
) => {
  let currentScene = !!scene ? scene : EngineStore.LastCreatedScene;
  if (!currentScene) return;

  let animations: Animation[] = [];

  // * 입력한 Property 각각 Animation을 생성합니다.
  if (Array.isArray(input.property)) {
    const generatedAnims = input.property.flatMap((property) => {
      const anim = generateAnimationByProperty(property, input.easingFunction, loop);
      if (anim) return anim;
      else return [];
    });
    animations.push(...generatedAnims);
  } else {
    const generatedAnim = generateAnimationByProperty(input.property, input.easingFunction, loop);
    if (generatedAnim) animations.push(generatedAnim);
  }

  // * 생성된 애니메이션 중 가장 긴 Frame을 구합니다.
  let maxFrame = 0;
  for (let i = 0; i < animations.length; i++) {
    animations[i]?.getKeys().forEach((keyFrame) => {
      maxFrame = Math.max(keyFrame.frame, maxFrame);
    });
  }

  // * 생성된 애니메이션을 입력한 Target에 적용합니다.
  if (Array.isArray(input.target)) {
    const animatables = input.target.flatMap((target) =>
      !!currentScene ? playAnimationTarget(currentScene, target, animations, maxFrame, stopCurrent, loop) : []
    );

    // * 입력한 콜백이 있으면, await 후에 호출합니다.
    if (!!input.onAnimationEnd) {
      await Promise.all(animatables.map((animatable) => animatable.waitAsync()));
      input.onAnimationEnd();
    }
  } else {
    const animatable = playAnimationTarget(currentScene, input.target, animations, maxFrame, stopCurrent, loop);
    if (!!input.onAnimationEnd) {
      await animatable.waitAsync();
      input.onAnimationEnd();
    }
  }
};

//-------------------------------------------------
export const generateAnimation = (targetProperty: string, animationType: number, loop: boolean) => {
  return new Animation(
    targetProperty,
    targetProperty,
    ANIMATION_TARGET_FRAME,
    animationType,
    loop ? Animation.ANIMATIONLOOPMODE_CYCLE : Animation.ANIMATIONLOOPMODE_CONSTANT
  );
};
const generateAnimationByProperty = <T>(
  param: AnimationProperty<T>,
  easingFunction?: EasingFunction,
  loop: boolean = false
) => {
  if (typeof param.targetProperty !== 'string') return;

  let type = 0;
  if (Array.isArray(param.keyFrame)) {
    if (param.keyFrame[0]) type = getAnimationType(param.keyFrame[0].from);
  } else {
    type = getAnimationType(param.keyFrame.from);
  }
  const animation = generateAnimation(param.targetProperty, type, loop);
  if (easingFunction) animation.setEasingFunction(easingFunction);

  if (Array.isArray(param.keyFrame)) {
    let start = 0;
    param.keyFrame.forEach((keyFrame) => {
      let frame = 0;
      if (keyFrame.duration !== undefined) {
        frame = timeToFrame(keyFrame.duration);
      } else if (keyFrame.frame !== undefined) {
        frame = keyFrame.frame;
      }
      setAnimationKeyFrame(animation, keyFrame, start, start + frame);
      start += frame;
    });
  } else {
    let frame = 0;
    if (param.keyFrame.duration !== undefined) {
      frame = timeToFrame(param.keyFrame.duration);
    } else if (param.keyFrame.frame !== undefined) {
      frame = param.keyFrame.frame;
    }
    setAnimationKeyFrame(animation, param.keyFrame, 0, frame);
  }
  return animation;
};
const setAnimationKeyFrame = (animation: Animation, keyFrame: AnimationKeyFrame, start: number, end: number) => {
  animation.setKeys([
    { frame: start, value: keyFrame.from },
    { frame: end, value: keyFrame.to },
  ]);
};
const playAnimationTarget = (
  scene: Scene,
  target: AnimationTarget,
  animations: Animation[],
  frame: number,
  stopCurrent: boolean,
  loop: boolean
) => {
  target.animations = animations;

  const animatable = scene.beginAnimation(target, 0, frame, loop, 1, undefined, undefined, stopCurrent);
  animatable.disposeOnEnd = true;

  return animatable;
};
const TYPEOF_NUMBER = 'number';
const getAnimationType = (value: AnimationValue) => {
  if (typeof value === TYPEOF_NUMBER) {
    return Animation.ANIMATIONTYPE_FLOAT;
  } else if (value instanceof Vector2) {
    return Animation.ANIMATIONTYPE_VECTOR2;
  } else if (value instanceof Vector3) {
    return Animation.ANIMATIONTYPE_VECTOR3;
  } else if (value instanceof Color3) {
    return Animation.ANIMATIONTYPE_COLOR3;
  }

  return Animation.ANIMATIONTYPE_FLOAT;
};
//-------------------------------------------------
export const timeToFrame = (seconds: number) => {
  return Math.round(seconds * ANIMATION_TARGET_FRAME);
};

export const stopPlayingAnimation = (scene: Scene, target: AnimationTarget) => {
  const animatable = scene?.getAnimatableByTarget(target);
  if (!animatable) return;
  animatable.stop();
  return;
};

